/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: How to use section for AI Product Avatar landing page
 */
import Image from 'next/image';
import React from 'react';

interface StepData {
  number: number;
  title: string;
  description: string;
}

const steps: StepData[] = [
  {
    number: 1,
    title: 'Pick Avatar & Add Product',
    description:
      'Choose an avatar from the library or upload your own image. Then add a clear product photo so the AI can place it naturally in the scene.',
  },
  {
    number: 2,
    title: 'Add Script & Voice',
    description:
      'Type your script for the voiceover and select an AI voice that matches your tone. You can also upload your own audio if you prefer a custom delivery.',
  },
  {
    number: 3,
    title: 'Set Video Format',
    description:
      'Adjust your video layout by choosing a subtitle style, selecting a background option, and picking the aspect ratio that fits your target platform.',
  },
  {
    number: 4,
    title: 'Choose Pose & Preview Your Video',
    description:
      'Select how the avatar should hold or present your product, then start the generation. Once the video is finished, you can preview and download the final result.',
  },
];

const StepItem: React.FC<StepData> = ({ number, title, description }) => (
  <div className="flex gap-4">
    {/* Step Number */}
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center text-white font-semibold text-sm">
      {number}
    </div>
    {/* Step Content */}
    <div className="flex flex-col gap-2">
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          How to Create a Product Video with AI Avatar
        </h2>

        {/* Content Area */}
        <div className="flex flex-col pad:flex-row gap-8 pad:gap-12 sm:gap-16 items-center">
          {/* Left Image */}
          <div className="w-full pad:w-1/2">
            <div className="relative rounded-2xl overflow-hidden shadow-lg bg-[#F8F9FC]">
              <Image
                src="https://assets.falcocut.ai/ai-product-avatar/how-to-create-a-product-video-with-ai-avatar.webp"
                alt="How to Create a Product Video with AI Avatar"
                width={560}
                height={480}
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* Right Steps */}
          <div className="w-full pad:w-1/2 flex flex-col gap-6 pad:gap-8">
            {steps.map((step) => (
              <StepItem key={step.number} {...step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
